import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Search } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import AddressAutocomplete, { AddressFields, loadGoogleMapsScript } from '../AddressAutocomplete';
import UnsavedChangesBar from './UnsavedChangesBar';
import { SettingsSheet, SheetBlock, SheetField, SheetLocked, SheetRow, SheetSection } from './sheet';
import { INPUT_CLS, SettingsSwitch, StoreLoadState, StoreRow, useStoreSettingsForm } from './storeSettingsForm';

/**
 * Κατάστημα → «Στοιχεία επιχείρησης» (12/10/2026, από την παλιά SettingsPage): ό,τι τυπώνεται στην
 * κεφαλίδα της απόδειξης (επωνυμία, ΑΦΜ, ΔΟΥ, δραστηριότητα, διεύθυνση, υποκατάστημα/έδρα), η
 * επικοινωνία και το κλειδί Google Maps. Επωνυμία και ΑΦΜ μόνο για ανάγνωση, όπως πριν.
 * Η αρίθμηση παραστατικών (Oxygen) είναι από 13/10/2026 δική της επιλογή: Ταμείο → «Αρίθμηση παραστατικών».
 * Διάταξη «Φύλλο» (σχέδιο A, 13/10/2026): μία ενότητα ανά θέμα, ευρετήριο αριστερά.
 */

interface BusinessForm {
  doy: string;
  activity: string;
  address: string;
  street_address: string;
  street_number: string;
  area: string;
  city: string;
  postal_code: string;
  country: string;
  formatted_address: string;
  latitude: number | null;
  longitude: number | null;
  is_branch: boolean;
  branch_code: string;
  headquarters_address: string;
  headquarters_street_address: string;
  headquarters_street_number: string;
  headquarters_city: string;
  headquarters_postal_code: string;
  headquarters_area: string;
  headquarters_country: string;
  headquarters_formatted_address: string;
  headquarters_latitude: number | null;
  headquarters_longitude: number | null;
  phone_number: string;
  email: string;
  google_maps_api_key: string;
}

const EMPTY: BusinessForm = {
  doy: '', activity: '',
  address: '', street_address: '', street_number: '', area: '', city: '', postal_code: '', country: 'GR',
  formatted_address: '', latitude: null, longitude: null,
  is_branch: false, branch_code: '0',
  headquarters_address: '', headquarters_street_address: '', headquarters_street_number: '', headquarters_city: '',
  headquarters_postal_code: '', headquarters_area: '', headquarters_country: 'GR', headquarters_formatted_address: '',
  headquarters_latitude: null, headquarters_longitude: null,
  phone_number: '', email: '', google_maps_api_key: '',
};

// Ποια πεδία ανήκουν σε κάθε ενότητα (τελεία στο ευρετήριο όταν κάποιο άλλαξε).
const SECTION_KEYS: Record<'receipt' | 'address' | 'branch' | 'contact' | 'maps', Array<keyof BusinessForm>> = {
  receipt: ['doy', 'activity'],
  address: ['address', 'street_address', 'street_number', 'area', 'city', 'postal_code', 'country', 'formatted_address', 'latitude', 'longitude'],
  branch: [
    'is_branch', 'branch_code', 'headquarters_address', 'headquarters_street_address', 'headquarters_street_number',
    'headquarters_city', 'headquarters_postal_code', 'headquarters_area', 'headquarters_country',
    'headquarters_formatted_address', 'headquarters_latitude', 'headquarters_longitude',
  ],
  contact: ['phone_number', 'email'],
  maps: ['google_maps_api_key'],
};

// Η PG στέλνει τα numeric ως κείμενο· 0/κενό = χωρίς συντεταγμένες, όπως πριν.
const coord = (v: unknown): number | null => {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const fromStore = (s: StoreRow): BusinessForm => ({
  doy: s.doy || '',
  activity: s.activity || '',
  address: s.address || '',
  street_address: s.street_address || '',
  street_number: s.street_number || '',
  area: s.area || '',
  city: s.city || '',
  postal_code: s.postal_code || '',
  country: s.country || 'GR',
  formatted_address: s.formatted_address || '',
  latitude: coord(s.latitude),
  longitude: coord(s.longitude),
  is_branch: s.is_branch === true,
  branch_code: s.branch_code || '0',
  headquarters_address: s.headquarters_address || '',
  headquarters_street_address: s.headquarters_street_address || '',
  headquarters_street_number: s.headquarters_street_number || '',
  headquarters_city: s.headquarters_city || '',
  headquarters_postal_code: s.headquarters_postal_code || '',
  headquarters_area: s.headquarters_area || '',
  headquarters_country: s.headquarters_country || 'GR',
  headquarters_formatted_address: s.headquarters_formatted_address || '',
  headquarters_latitude: coord(s.headquarters_latitude),
  headquarters_longitude: coord(s.headquarters_longitude),
  phone_number: s.phone_number || '',
  email: s.email || '',
  google_maps_api_key: s.google_maps_api_key || '',
});

// Η ενότητα έχει ήδη τίτλο «Διεύθυνση καταστήματος»: η ετικέτα της αναζήτησης μένει μόνο για τους
// αναγνώστες οθόνης και ο μεγεθυντικός φακός μπαίνει μέσα στο πεδίο· ο αριθμός/Τ.Κ./χώρα σε στενή
// στήλη, όπως στο δείγμα.
const ADDRESS_NARROW = '[&>div:nth-child(2)]:grid-cols-[minmax(0,1fr)_7rem]';
const ADDRESS_NO_LABEL = `${ADDRESS_NARROW} [&>div:first-child>label]:sr-only [&>div:first-child>input]:pl-10`;

const BusinessDetailsPage: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { store } = useAuth();
  const { form, row, status, retry, saving, save } = useStoreSettingsForm({ guardId: 'store-settings:business', empty: EMPTY, fromStore });
  const { values: v, setField, setValues, changedKeys } = form;
  const [showKey, setShowKey] = useState(false);

  // Το autocomplete διευθύνσεων θέλει το Google Maps (με το κλειδί του καταστήματος, αν έχει).
  useEffect(() => {
    if (!window.google) loadGoogleMapsScript(store?.google_maps_api_key || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changed = (key: keyof BusinessForm) => changedKeys.includes(key);
  const sectionDirty = (section: keyof typeof SECTION_KEYS) => SECTION_KEYS[section].some(changed);

  const toggleBranch = (next: boolean) => {
    // Όχι υποκατάστημα → κωδικός 0 (και ο server σβήνει την έδρα).
    setValues(prev => ({ ...prev, is_branch: next, branch_code: next ? prev.branch_code : '0' }));
  };

  return (
    <SettingsSheet eyebrow={t('settings:nav.groups.store')} title={t('settings:business.title')} subtitle={t('settings:business.subtitle')}>
      {status !== 'ready' ? (
        <SheetBlock><StoreLoadState status={status} onRetry={retry} /></SheetBlock>
      ) : (
        <fieldset disabled={saving} className="min-w-0">
          <SheetSection id="business-receipt" title={t('settings:business.receiptCard')} hint={t('settings:page.fiscalSubtitle')}
            dirty={sectionDirty('receipt')}>
            <div className="xs-grid2">
              <SheetLocked label={t('settings:business.legalName')} value={row?.store_name || row?.name || ''} />
              <SheetLocked label={t('settings:vatNumber')} value={row?.vat_number || ''} />
            </div>
            <p className="xs-note mb-5">{t('settings:business.identityHint')}</p>
            <div className="xs-grid2">
              <SheetField label={t('settings:page.doyLabel')} htmlFor="business-doy" changed={changed('doy')}>
                <input id="business-doy" type="text" value={v.doy} onChange={e => setField('doy', e.target.value)}
                  className={INPUT_CLS} placeholder={t('settings:page.doyPlaceholder')} autoComplete="one-time-code" />
              </SheetField>
              <SheetField label={t('settings:page.activityLabel')} htmlFor="business-activity" changed={changed('activity')}>
                <input id="business-activity" type="text" value={v.activity} onChange={e => setField('activity', e.target.value)}
                  className={INPUT_CLS} placeholder={t('settings:page.activityPlaceholder')} autoComplete="one-time-code" />
              </SheetField>
            </div>
          </SheetSection>

          <SheetSection id="business-address" title={t('settings:business.storeAddress')} indexLabel={t('settings:business.addressIndex')}
            hint={t('settings:business.addressHint')} dirty={sectionDirty('address')}>
            <div className="relative">
              <Search aria-hidden="true" className="pointer-events-none absolute left-3.5 top-[13px] w-4 h-4 text-t4" />
              {/* key: το πεδίο αναζήτησης κρατά defaultValue — μετά από «Αναίρεση» ξαναστήνεται */}
              <AddressAutocomplete
                key={`store-${form.version}`}
                className={ADDRESS_NO_LABEL}
                label={t('settings:business.storeAddress')}
                placeholder={t('settings:address.searchPlaceholder')}
                value={{
                  address: v.address, street_address: v.street_address, street_number: v.street_number,
                  city: v.city, postal_code: v.postal_code, area: v.area, country: v.country,
                  formatted_address: v.formatted_address, latitude: v.latitude, longitude: v.longitude,
                }}
                onChange={(f: AddressFields) => setValues(prev => ({
                  ...prev,
                  address: f.address ?? prev.address,
                  street_address: f.street_address ?? prev.street_address,
                  street_number: f.street_number ?? prev.street_number,
                  city: f.city ?? prev.city,
                  postal_code: f.postal_code ?? prev.postal_code,
                  area: f.area ?? prev.area,
                  country: f.country ?? prev.country,
                  formatted_address: f.formatted_address ?? prev.formatted_address,
                  latitude: f.latitude ?? prev.latitude,
                  longitude: f.longitude ?? prev.longitude,
                }))}
              />
            </div>
          </SheetSection>

          <SheetSection id="business-branch" title={t('settings:page.branchLabel')} hint={t('settings:page.branchHint')}
            dirty={sectionDirty('branch')}>
            <SheetRow
              title={t('settings:business.isBranch')}
              subtitle={v.is_branch ? t('settings:business.branchYes') : t('settings:business.branchNo')}
              changed={changed('is_branch')}
              control={({ labelId, descriptionId }) => (
                <SettingsSwitch checked={v.is_branch} onChange={toggleBranch} labelId={labelId} describedById={descriptionId} />
              )}
            />
            {v.is_branch && (
              <div className="mt-5 pt-5 border-t border-dashed border-line space-y-4">
                <SheetField label={t('settings:page.branchCodeLabel')} htmlFor="business-branch-code" changed={changed('branch_code')}>
                  <input id="business-branch-code" type="text" value={v.branch_code} onChange={e => setField('branch_code', e.target.value)}
                    className={`${INPUT_CLS} max-w-[12rem]`} placeholder={t('settings:page.branchCodePlaceholder')} autoComplete="one-time-code" />
                </SheetField>
                <AddressAutocomplete
                  key={`hq-${form.version}`}
                  className={ADDRESS_NARROW}
                  label={t('settings:page.hqAddressLabel')}
                  placeholder={t('settings:page.hqAddressPlaceholder')}
                  labelPrefix={t('settings:page.hqLabelPrefix')}
                  value={{
                    address: v.headquarters_address, street_address: v.headquarters_street_address,
                    street_number: v.headquarters_street_number, city: v.headquarters_city,
                    postal_code: v.headquarters_postal_code, area: v.headquarters_area, country: v.headquarters_country,
                    formatted_address: v.headquarters_formatted_address,
                    latitude: v.headquarters_latitude, longitude: v.headquarters_longitude,
                  }}
                  onChange={(f: AddressFields) => setValues(prev => ({
                    ...prev,
                    headquarters_address: f.address ?? prev.headquarters_address,
                    headquarters_street_address: f.street_address ?? prev.headquarters_street_address,
                    headquarters_street_number: f.street_number ?? prev.headquarters_street_number,
                    headquarters_city: f.city ?? prev.headquarters_city,
                    headquarters_postal_code: f.postal_code ?? prev.headquarters_postal_code,
                    headquarters_area: f.area ?? prev.headquarters_area,
                    headquarters_country: f.country ?? prev.headquarters_country,
                    headquarters_formatted_address: f.formatted_address ?? prev.headquarters_formatted_address,
                    headquarters_latitude: f.latitude ?? prev.headquarters_latitude,
                    headquarters_longitude: f.longitude ?? prev.headquarters_longitude,
                  }))}
                />
              </div>
            )}
          </SheetSection>

          <SheetSection id="business-contact" title={t('settings:business.contactCard')} hint={t('settings:business.contactCardHint')}
            dirty={sectionDirty('contact')}>
            <div className="xs-grid2">
              <SheetField label={t('common:phone')} htmlFor="business-phone" changed={changed('phone_number')}>
                <input id="business-phone" type="tel" value={v.phone_number} onChange={e => setField('phone_number', e.target.value)}
                  className={INPUT_CLS} placeholder={t('settings:page.phonePlaceholder')} autoComplete="one-time-code" />
              </SheetField>
              <SheetField label={t('common:email')} htmlFor="business-email" changed={changed('email')}>
                <input id="business-email" type="email" value={v.email} onChange={e => setField('email', e.target.value)}
                  className={INPUT_CLS} placeholder={t('settings:page.emailStorePlaceholder')} autoComplete="one-time-code" />
              </SheetField>
            </div>
          </SheetSection>

          <SheetSection id="business-maps" title={t('settings:business.mapsTitle')} hint={t('settings:page.mapsKeyHint')}
            dirty={sectionDirty('maps')}>
            <SheetField label={t('settings:business.mapsKeyLabel')} htmlFor="business-maps-key" changed={changed('google_maps_api_key')}
              className="max-w-md">
              <div className="relative">
                <input id="business-maps-key" type={showKey ? 'text' : 'password'} value={v.google_maps_api_key}
                  onChange={e => setField('google_maps_api_key', e.target.value)} spellCheck={false}
                  className={`${INPUT_CLS} font-mono pr-12`} placeholder="AIzaSy..." autoComplete="one-time-code" />
                <button type="button" onClick={() => setShowKey(s => !s)}
                  aria-label={showKey ? t('settings:business.hideKey') : t('settings:business.showKey')}
                  title={showKey ? t('settings:business.hideKey') : t('settings:business.showKey')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-t3 hover:text-t1 hover:bg-surface-3 transition-colors">
                  {showKey ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                </button>
              </div>
            </SheetField>
          </SheetSection>
        </fieldset>
      )}

      <UnsavedChangesBar dirty={form.dirty} saving={saving} onSave={() => save()} onReset={() => form.reset()} />
    </SettingsSheet>
  );
};

export default BusinessDetailsPage;
